/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import appConfig from 'app-config';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { MLModalService } from 'ml-portals-services-react';
import { UserService, MLAgentChoiceReportService } from 'gw-capability-gateway';
import { platformMessages } from 'ml-components-platform-react';
import _ from 'lodash';
import {
    MLQuoteNameUtil, MLCapitalizeStringFilter, MLLineTypeUtil
} from 'ml-portals-util-js';
import { MLTagManagerService } from 'ml-portals-services-react'
import PATrackEvent from '../../components/TrackEvent/PATrackEvent';
import metadata from './GatewayConfirmationPage.metadata.json5';
import messages from './GatewayConfirmationPage.messages';
import styles from './GatewayConfirmationPage.module.scss';
import PUMBTrackEvent from '../../components/TrackEvent/PUMBTrackEvent';
import HOTrackEvent from '../../components/TrackEvent/HOTrackEvent';

import { TooltipIcon  } from '@jutro/components';

import { IntlContext } from '@jutro/legacy/locale';

function ConfirmationPage(props) {
    const isGPA = appConfig.portalCode === 'GPA';
    const { wizardData: submissionVM, history } = props;
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const intl = useContext(IntlContext);
    const { authHeader } = useAuthentication();

    const {
        showProgressDialog
    } = MLModalService();

    const {
        showReportModalWithQuoteID
    } = MLAgentChoiceReportService();

    const ratingPaymentMethodTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('RatingPaymentMethod_ML');

    const [policyDetails, setPolicyDetails] = useState({});
    const [policySummary, setPolicySummary] = useState({});

    const isAutoPolicy = (policyDetail) => {
        return policyDetail.lines.some((line) => MLLineTypeUtil.isPersonalAuto(line));
    };

    const isHomePolicy = (policyDetail) => {
        return policyDetail.lines.some((line) => MLLineTypeUtil.isHomeOwners(line));
    };

    const isUmbrellaPolicy = (policyDetail) => {
        return policyDetail.lines.some((line) => MLLineTypeUtil.isPersonalUmbrella(line));
    };

    const getProductName = (policyDetail) => {
        let productName = '';
        if (isHomePolicy(policyDetail)) {
            productName = MLQuoteNameUtil.getCoveragePartTypeName(policyDetail.coveragePartType);
        } else if (isAutoPolicy(policyDetail)) {
            productName = 'Auto'
        } else if (isUmbrellaPolicy(policyDetail)) {
            productName = 'Umbrella';
        }

        return productName;
    };

    const assignInitValues = (details) => {
        const paymentMethodTypeKey = ratingPaymentMethodTypelist.getCode(details.paymentDetails.ratingPaymentMethod_ML).name;

        setPolicySummary({
            periodStartDate: details.effective_ML,
            periodEndDate: details.expiration_ML,
            policyNumber: details.policyId,
            openContingencies: details.openContingencies,
            paymentDetails: details.paymentDetails,
            paymentDate: details.paymentDetails.paymentReceivedLocalDate,
            accountHolder: details.policyHolder,
            dob: details.policyHolder.dateOfBirth,
            coinsured: details.secondaryInsured_ML,
            policyAddress: details.policyAddress,
            productName: getProductName(details),
            paData: isAutoPolicy(details) ? details.lobs.personalAuto.coverables.vehicles : details.vehicles_ML,
            isAuto: isAutoPolicy(details),
            isHome: isHomePolicy(details),
            isUmbrella: isUmbrellaPolicy(details),
            mailingAddress: details.mailingAddress,
            paymentMethod: translator({
                id: paymentMethodTypeKey,
                defaultMessage: paymentMethodTypeKey
            }),
            deedInfo_Ext: details?.deedInfo_Ext,
            isModernProduct_Ext: details?.isModernProduct_Ext
        });
    };

    const showAgentChoiceModal = (details) => {
        if (details.length === 1) {
            const policyDetail = details[0];
            if (policyDetail.isChoiceAgentSubmission_ML) {
                UserService.getCurrentUser().then((user) => {
                    if (user.isChoiceAgent_ML) {
                        showReportModalWithQuoteID(policyDetail.quoteId);
                    }
                });
            }
        }
    };

    const callGetBoundSubmissionDetails = (request, callback) => {
        return LoadSaveService.getBoundSubmissionDetails(request, authHeader)
            .then((boundSubmissions) => {
                callback(boundSubmissions);
            }).catch(() => {
                callback(undefined);
            });
    };

    const populatePolicySummariesWhenBound = (policy) => {
        const request = {
            quoteID: policy.quoteId,
            sessionUUID: submissionVM.sessionUUID.value,
            excludeCoverages: true
        };

        const promise = new Promise((resolve) => {
            callGetBoundSubmissionDetails(request, (results) => {
                resolve();
                if (!_.isNil(results)) {
                    setPolicyDetails(results);
                    assignInitValues(results);
                }
            });
        });
        showProgressDialog(platformMessages.loading, promise);
    };

    const retrieveSubmission = () => {
        const submissionDetailsPromise = LoadSaveService.getSubmissionDetails({
            quoteID: submissionVM.quoteID.value,
            sessionUUID: submissionVM.sessionUUID.value,
            excludeCoverages: true
        }, authHeader).then((details) => {
            setTimeout(() => {
                setPolicyDetails(details);
                assignInitValues(details);
                populatePolicySummariesWhenBound(details);
                const attributes = {product: submissionVM.baseData.productCode.value};
                if (details) {
                    attributes.productAmount = details.paymentDetails.amountCharged.amount;
                    const typekey = ratingPaymentMethodTypelist.getCode(details.paymentDetails.ratingPaymentMethod_ML).name;
                    attributes.productMethod = translator({
                        id: typekey,
                        defaultMessage: typekey
                    });
                    attributes.confirmationID = details.paymentDetails.confirmationNumber;
                    attributes.policyNumber = details.policyId;
                }
                MLTagManagerService.sendEvent('purchasePolicy', attributes);
                showAgentChoiceModal(policyDetails);
            }, 100);
        });

        showProgressDialog(platformMessages.loading, submissionDetailsPromise);
    }
    useEffect( () => {
        if (isGPA) {
            UserService.getCurrentUser(authHeader).then((user) => {
                if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
                    HOTrackEvent('Quote Purchased', false, submissionVM, user, { step: 'Policy Issued' });
                } 
                else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
                    PUMBTrackEvent('Quote Purchased', false, submissionVM, user, { step: 'Policy Issued' });}
                else {
                    PATrackEvent('Quote Purchased', false, submissionVM, user, { step: 'Policy Issued' });
                }
            });
        }
    }, []);

    useEffect(() => {
        retrieveSubmission();
    }, []);

    const isMortgageePayFirstYear = (paymentDetails) => {
        if (!paymentDetails) {
            return false;
        }
        return paymentDetails.isPayThroughMortgage && paymentDetails.firstYear;
    };

    useEffect(() => {
        setTimeout(()=>{
            const element = document.getElementById("printConfirmationButton");
            if(element){
                element.focus();
            }
        }, 100)
  }, []);

    const getFullName = (person) => {
        const nameSuffixList = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
        const middleName = (person.middleName) ? person.middleName : '';
        const name = _.startCase(_.toLower(`${person.name} ${middleName} ${person.lastName}`));
        const suffixTypeKeyName = (person.suffix) ? nameSuffixList.getCode(person.suffix).name : '';
        const suffixDisplayName = translator(
            {
                id: suffixTypeKeyName,
                defaultMessage: suffixTypeKeyName
            }
        );
        return `${name} ${suffixDisplayName}`;
    };

    const printConfirmation = () => {
        window.print();
        MLTagManagerService.sendEvent('linkClick', {linkName: 'printConfirmation', product: submissionVM.baseData.productCode.value});
    };

    const goToAccount = () => {
        history.push(`/accounts/${_.get(submissionVM, 'baseData.accountNumber.value')}/summary`);
    };

    const formatAmountString = useCallback((amount) => {
        if (_.isNil(amount)) {
            return '';
        }

        try {
            return intl.formatNumber(_.get(amount, 'amount', 0), { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const getContingenciesTableData = () => {
        const openOrClosedContingencies = _.get(policyDetails, 'openOrClosedContingencies', []);
        const contingenciesData = [
            {
                title: "Watch your email inbox for more information.",
                docRetentionStatus: "Information"
            },
            {
                title: "Remember to notify your current carrier of your new policy with us so they can cancel your policy on the effective date listed above.",
                docRetentionStatus: "Information"
            },
            ...openOrClosedContingencies
        ]

        return contingenciesData

    }

    const generateOverrides = () => {
        const openContingenciesOverride = getContingenciesTableData().map((openContingency, index) => ({
                [`contingencyTitle${index}`]: {
                    content: (<div>
                        <li>{openContingency.title} &nbsp;
                        {openContingency.description_ML && <TooltipIcon
                            text={openContingency.description_ML}
                            />}
                        </li>
                    </div>)
                },
                [`contingencyAction${index}`]: {
                    content: (<div>
                        <li>{openContingency.docRetentionStatus}
                        </li>
                    </div>)
                }              
            }));

        const vehicleDetailsOverride = _.get(policySummary, 'paData', []).map((vehicle, index) => {
            return {
                [`vehicleDetails${index}`]: {
                    content: vehicle.namedNonOwner_ML ? 
                        translator(messages.vehicleNamedNonOwner) :
                        (
                            <>
                                <strong>{vehicle.year} {vehicle.make}</strong> {vehicle.model} {vehicle.trim_ML}
                            </>
                        )
                }
            };
        });

        return Object.assign({}, ...openContingenciesOverride, ...vehicleDetailsOverride);
    };

    const resolvers = {
        resolveCallbackMap: {
            printConfirmation: printConfirmation,
            goToAccount: goToAccount
        },
        resolveClassNameMap: styles
    };

    const convertLocalDateToDate = (localDate) => {
        if (_.isNil(localDate)) {
            return '';
        }
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    };

    const overrides = {
        openContingenciesIterable: {
            data: getContingenciesTableData()
        },
        alertMessageContainer: {
            message: (
                <strong>{translator(messages.alertMessage, { productName: policySummary.productName })}</strong>)
        },
        policyDetailsPolicyNumber: {
            content: translator(messages.policyDetailsPolicyNumber, { policyNumber: policySummary.policyNumber })
        },
        effectiveDate: {
            content: translator(messages.periodStartDate, { effectiveDate: convertLocalDateToDate(policySummary.periodStartDate) })
        },
        expirationDate: {
            content: translator(messages.periodExpirationDate, { expirationDate: convertLocalDateToDate(policySummary.periodEndDate) })
        },
        selectedPaymentMethod: {
            content: policySummary.paymentMethod
        },
        selectedPaymentMethodLast4Digits: {
            visible: _.get(policySummary, 'paymentDetails.last4Digits') !== undefined,
            content: translator(messages.endingInXXX, { cardNumber: _.get(policySummary, 'paymentDetails.last4Digits') })
        },
        downPaymentContainer: {
            visible: !isMortgageePayFirstYear(policySummary.paymentDetails) && _.get(policySummary, 'paymentDetails.amountCharged.amount', 0) > 0
        },
        downPaymentAmount: {
            content: `${translator(messages.paymentAmount)} ${formatAmountString(_.get(policySummary, 'paymentDetails.amountCharged'))}`
        },
        downPaymentDate: {
            content: `${translator(messages.paymentDate)} ${convertLocalDateToDate(policySummary.paymentDate)}`
        },
        confirmationNumber: {
            visible: _.get(policySummary, 'paymentDetails.confirmationNumber') !== undefined,
            content: `${translator(messages.confirmationNumber)} ${_.get(policySummary, 'paymentDetails.confirmationNumber')}`
        },
        downPaymentMethodLabel: {
            content: translator(messages.downPaymentMethod)
        },
        downPaymentMethod: {
            content: _.get(policySummary, 'paymentDetails.downPaymentMethod')
        },
        downPaymentMethodLast4Digits: {
            visible: _.get(policySummary, 'paymentDetails.downPaymentLast4Digits') !== undefined,
            content: translator(messages.endingInXXX, { cardNumber: _.get(policySummary, 'paymentDetails.downPaymentLast4Digits') })
        },
        primaryInsuredFullName: {
            content: <label>{getFullName(_.get(policySummary, 'accountHolder', {}))}</label>
        },
        addressLine1: {
            content: _.get(policySummary, 'mailingAddress.addressLine1')
        },
        cityStateZip: {
            content: `${_.get(policySummary, 'mailingAddress.city', '')}, ${_.get(policySummary, 'mailingAddress.state', '')} ${_.get(policySummary, 'mailingAddress.postalCode', '')}`
        },
        coInsuredContainer: {
            visible: _.get(policySummary, 'coinsured') !== undefined && 
            !(policySummary?.isModernProduct_Ext === true && policySummary.isHome === true && policySummary?.deedInfo_Ext === true)
        },
        coInsuredFullName: {
            content: <label>{ getFullName(_.get(policySummary, 'coinsured', {})) }</label>
        },
        personalAutoVehicleContainer: {
            visible: policySummary.isAuto === true
        },
        vehiclesIterable: {
            data: _.get(policySummary, 'paData', [])
        },
        homeOwnersPropertyContainer: {
            visible: policySummary.isHome === true
        },
        propertyAddressLine1: {
            content: _.get(policySummary, 'policyAddress.addressLine1')
        },
        propertyCityStateZip: {
            content: `${_.get(policySummary, 'policyAddress.city', '')}, ${_.get(policySummary, 'policyAddress.state', '')} ${_.get(policySummary, 'policyAddress.postalCode', '')}`
        },
        ...generateOverrides()
    };

    return (
        <WizardPage
            showPrevious={false}
            showNext={false}
            showCancel={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

ConfirmationPage.propTypes = wizardProps;
export default withRouter(ConfirmationPage);
